<!-- 
Date/time picker component.

Allows dates and times to be typed in. 
The date portion will display a date picker when clicked.
Originally we displayed a time picker when the time portion was clicked but it was more trouble than
it was worth so now time can only be entered by typing it in.
-->
<template>
  <v-container :id="id" grid-list-md pa-0 fluid>
    <v-layout wrap>
      <v-flex xs6>
        <v-menu
          :id="_uid.toString()"
          v-model="showDatePicker"
          :close-on-content-click="false"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            ref="date"
            :value="dateFormatted"
            :label="label + ' (date)'"
            :rules="[rules.validDate, required ? rules.required : rules.empty]"
            :validate-on-blur="validateDateOnBlur"
            :autofocus="autofocus"
            :outline="outline"
            :single-line="outline"
            :class="{'compact': outline}"
            clearable
            placeholder="DD/MM/YYYY"
            class="our-date"
            @input="clearDate"
            @change="dateEntered"
            @focus="dateGotFocus"
            @blur="dateLostFocus"
          />
          <v-date-picker 
            :value="date" 
            :min="dateMinValue"
            :max="dateMaxValue" 
            no-title
            scrollable
            @change="datePicked"
          />
        </v-menu>
      </v-flex>
      <v-flex xs6>
        <v-text-field
          ref="time"
          :value="timeFormatted"
          :label="label + ' (time)'"
          :rules="[rules.validTime, required ? rules.required : rules.empty]"
          :validate-on-blur="validateTimeOnBlur"
          :outline="outline"
          :single-line="outline"
          :class="{'compact': outline}"
          clearable
          placeholder="HH:MM"
          class="our-time"
          @input="clearTime"
          @change="timeEntered"
          @focus="timeGotFocus"
          @blur="timeLostFocus"
        />
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import moment from "moment";
import ruleMixins from "@/mixins/rule.mixins";

export default {
  mixins: [ruleMixins],
  props: {
    id: {
      default: "",
      type: String
    },
    value: {
      default: "",
      type: String
    },
    label: {
      default: "",
      type: String
    },
    min: {
      default: "1900-01-01",
      type: String
    },
    max: {
      default: "2099-12-31",
      type: String
    },
    required: {
      default: false,
      type: Boolean
    },
    currentOrPast: {
      default: false,
      type: Boolean
    },
    currentOrFuture: {
      default: false,
      type: Boolean
    },
    autofocus: {
      default: false,
      type: Boolean
    },
    outline: {
      default: false,
      type: Boolean
    }
  },
  data() {
    return {
      showDatePicker: false,
      validateDateOnBlur: false,
      validateTimeOnBlur: false,
      dateMinValue: "",
      dateMaxValue: ""
    };
  },
  computed: {
    date() {
      return this.value && this.rules.validDate(this.value) ? this.formatDateForPicker(this.value) : "";
    },
    time() {
      return this.hasTime(this.value) ? this.formatTimeForPicker(this.value) : "";
    },
    dateFormatted() {
      return this.value && this.rules.validDate(this.value) ? this.formatDateForDisplay(this.value) : "";
    },
    timeFormatted() {
      return this.hasTime(this.value) ? this.formatTimeForDisplay(this.value) : "";
    },
    valid() {
      return this.$refs.date.valid && this.$refs.time.valid;
    }
  },
  mounted() {
    this.dateMinValue = this.formatDateForPicker(
      this.currentOrFuture ? moment() : moment(this.min, this.dateFormatsAccepted)
    );
    this.dateMaxValue = this.formatDateForPicker(
      this.currentOrPast ? moment() : moment(this.max, this.dateFormatsAccepted)
    );
  },
  methods: {
    dateEntered(d) {
      if (d && this.rules.validDate(d) === true) {
        this.emit(d, this.time);
      }
    },
    datePicked(d) {
      this.showDatePicker = false;
      this.emit(d, this.time);
    },
    timeEntered(t) {
      if (t && this.rules.validTime(t) === true) {
        this.emit(this.date, t);
      }
    },
    clearDate(v) {
      if (!v) {
        this.emit("", this.time);
      }
    },
    clearTime(v) {
      if (!v) {
        this.emit(this.date, "");
      }
    },
    emit(d, t) {
      var s = "";
      if (!d && t) {
        d = moment();
      }
      if (d && this.rules.validDate(d) === true) {
        s += this.formatDateForPicker(d);
      }
      if (t && this.rules.validTime(t)) {
        s += "T" + this.formatTimeForPicker(t);
      }
      this.$emit("input", s);
    },
    timeGotFocus() {
      this.validateTimeOnBlur = true;
    },
    timeLostFocus() {
      this.$nextTick(function() {
        this.validateTimeOnBlur = false;
      });
    },
    dateGotFocus() {
      this.validateDateOnBlur = true;
    },
    dateLostFocus() {
      this.$nextTick(function() {
        this.validateDateOnBlur = false;
      });
    },
    hasTime(t) {
      return t && t.toString().indexOf("T") > 0 && this.rules.validTime(t) === true;
    },
    formatDateForDisplay(d) {
      return moment(d, this.dateFormatsAccepted).format(this.dateFormatForDisplay);
    },
    formatDateForPicker(d) {
      return moment(d, this.dateFormatsAccepted).format(this.dateFormatForPicker);
    },
    formatTimeForDisplay(d) {
      return moment(d, this.timeFormatsAccepted).format(this.timeFormatForDisplay);
    },
    formatTimeForPicker(d) {
      return moment(d, this.timeFormatsAccepted).format(this.timeFormatForPicker);
    }
  }
};
</script>
